import React from "react";
import {
  appendTestSessionId,
  getInitEndpoint,
  persistTestConfig,
  persistTestSessionId,
  updateGlobalTestConfig,
} from "../config";
import { getRuntimeLocale } from "../../lib/locale";
import { GuideOptionCard } from "../components/GuideOptionCard";
import { GuideResultCard, type RecommendedProduct } from "../components/GuideResultCard";

// -- Types -------------------------------------------------------------------

interface GuideResponse {
  session_id?: string;
  type: "question" | "result" | "error";
  question_id?: string;
  question?: string;
  explanation?: string;
  input_type?: string;
  options?: Array<Record<string, unknown>>;
  progress?: { current: number; total: number };
  recommended_products?: RecommendedProduct[];
  customer_message?: string;
  metadata?: {
    category: string;
    filters: Record<string, unknown>;
    merits?: Record<string, unknown>;
    products_considered: Array<{
      product_id: string;
      name: unknown;
      url: unknown;
      price: unknown;
      category: unknown;
    }>;
  };
  error?: string;
}

// -- API helper --------------------------------------------------------------

function guideEndpoint(apiBase: string): string {
  return apiBase.replace(/\/chat$/, "") + "/guide/ski";
}

function buildHeaders(token?: string): Record<string, string> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  try {
    const widgetVersion = (import.meta as any).env?.VITE_WIDGET_VERSION as string | undefined;
    if (widgetVersion) headers["X-Widget-Version"] = String(widgetVersion);
  } catch {}
  if (token) headers["Authorization"] = `Bearer ${token}`;
  return headers;
}

// -- Widget wrapper ----------------------------------------------------------

interface GuideWidgetProps {
  storeId: string;
  apiBase: string;
  showMetadata?: boolean;
}

export function GuideWidget({ storeId, apiBase, showMetadata = true }: GuideWidgetProps) {
  const tokenRef = React.useRef<string | null>(null);
  const apiBaseRef = React.useRef(apiBase);

  const refreshToken = React.useCallback(async (): Promise<string | null> => {
    try {
      const url = `${getInitEndpoint()}?store_id=${encodeURIComponent(storeId)}`;
      const urlWithSession = appendTestSessionId(url);
      const res = await fetch(urlWithSession, {
        method: "GET",
        credentials: "omit",
        cache: "no-store",
        mode: "cors",
      });
      if (!res.ok) return null;
      const data = await res.json();
      persistTestSessionId(data.test_session_id);
      persistTestConfig(data.test_config);
      updateGlobalTestConfig({ testSessionId: data.test_session_id, testConfig: data.test_config });
      const base = (data.api_base || "").replace(/\/$/, "");
      const fixedApiBase = /\/chat$/.test(base) ? base : `${base}/chat`;
      const nextToken = data.token as string | undefined;
      if (!nextToken) return null;
      const g = (window as any).__WW__ || {};
      (window as any).__WW__ = {
        ...g,
        storeId,
        apiBase: fixedApiBase,
        themeCssUrl: g.themeCssUrl,
        flags: data.flags,
        ...(data.test_session_id ? { testSessionId: data.test_session_id } : {}),
        ...(Array.isArray(data.test_config) ? { testConfig: data.test_config } : {}),
      };
      tokenRef.current = nextToken;
      apiBaseRef.current = fixedApiBase;
      return nextToken;
    } catch {
      return null;
    }
  }, [storeId]);

  const postGuide = React.useCallback(
    async (payload: Record<string, unknown>, signal?: AbortSignal): Promise<GuideResponse> => {
      if (!tokenRef.current) {
        const t = await refreshToken();
        if (!t) throw new Error("auth_failed");
      }

      const url = guideEndpoint(apiBaseRef.current);

      const attempt = () =>
        fetch(url, {
          method: "POST",
          headers: buildHeaders(tokenRef.current!),
          credentials: "omit",
          mode: "cors",
          cache: "no-store",
          body: JSON.stringify(payload),
          signal,
        });

      let res = await attempt();
      if ((res.status === 401 || res.status === 403)) {
        const t = await refreshToken();
        if (t) res = await attempt();
      }
      if (!res.ok) throw new Error(`guide: ${res.status}`);
      return res.json();
    },
    [refreshToken],
  );

  return <GuideWidgetInner storeId={storeId} postGuide={postGuide} showMetadata={showMetadata} />;
}

// -- Inner component ---------------------------------------------------------

type Phase = "intro" | "loading" | "question" | "result" | "error";
type LoadingMode = "question" | "recommendation";

interface InnerProps {
  storeId: string;
  postGuide: (payload: Record<string, unknown>, signal?: AbortSignal) => Promise<GuideResponse>;
  showMetadata: boolean;
}

function areAnswerSnapshotsEqual(a: Record<string, unknown>, b: Record<string, unknown>): boolean {
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  for (const key of aKeys) {
    if (!Object.prototype.hasOwnProperty.call(b, key)) return false;
    if (!Object.is(a[key], b[key])) return false;
  }
  return true;
}

function formatMetadataValue(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value)) {
    return value.map((item) => formatMetadataValue(item)).filter(Boolean).join(", ");
  }
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function formatAnswerLabel(key: string): string {
  const normalized = key
    .replace(/[_-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim();
  if (!normalized) return key;
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

const RECOMMENDATION_LOADING_PHRASES = ["Jämför", "Utvärderar", "Filtrerar", "Matchar", "Finjusterar"];
const GUIDE_WIDGET_LAYOUT_STYLE: React.CSSProperties = {
  width: "100%",
  maxWidth: "500px",
  margin: "0 auto",
};

function interpolateProgress(
  elapsedMs: number,
  fromMs: number,
  toMs: number,
  fromValue: number,
  toValue: number,
): number {
  if (elapsedMs <= fromMs) return fromValue;
  if (elapsedMs >= toMs) return toValue;
  const ratio = (elapsedMs - fromMs) / (toMs - fromMs);
  return fromValue + (toValue - fromValue) * ratio;
}

function computeRecommendationProgress(elapsedMs: number): number {
  if (elapsedMs <= 0) return 0;
  if (elapsedMs <= 5000) return interpolateProgress(elapsedMs, 0, 5000, 0, 90);
  if (elapsedMs <= 6000) return interpolateProgress(elapsedMs, 5000, 6000, 90, 95);
  if (elapsedMs <= 10000) {
    // Ease-in tail: slow first, then faster toward completion by 10s.
    const t = (elapsedMs - 6000) / 4000;
    return 95 + 5 * t * t;
  }
  return 100;
}

function RecommendationLoadingScreen({ startedAt, forceComplete }: { startedAt: number; forceComplete: boolean }) {
  const [elapsedMs, setElapsedMs] = React.useState(() =>
    Math.max(0, Date.now() - (startedAt || Date.now())),
  );
  const [phraseIndex, setPhraseIndex] = React.useState(0);

  React.useEffect(() => {
    setPhraseIndex(0);
    let timeoutId: number | null = null;
    let isCancelled = false;
    const scheduleNextPhrase = () => {
      if (isCancelled) return;
      const nextDelayMs = 1400 + Math.floor(Math.random() * 401); // 1400-1800 ms
      timeoutId = window.setTimeout(() => {
        if (isCancelled) return;
        setPhraseIndex((prev) => (prev + 1) % RECOMMENDATION_LOADING_PHRASES.length);
        scheduleNextPhrase();
      }, nextDelayMs);
    };
    scheduleNextPhrase();
    return () => {
      isCancelled = true;
      if (timeoutId != null) window.clearTimeout(timeoutId);
    };
  }, [startedAt]);

  React.useEffect(() => {
    const timer = window.setInterval(() => {
      setElapsedMs(Math.max(0, Date.now() - (startedAt || Date.now())));
    }, 200);
    return () => window.clearInterval(timer);
  }, [startedAt]);

  const progress = forceComplete ? 100 : computeRecommendationProgress(elapsedMs);
  const phrase = forceComplete
    ? "Klart"
    : (RECOMMENDATION_LOADING_PHRASES[phraseIndex] ?? RECOMMENDATION_LOADING_PHRASES[0]);

  return (
    <div
      className="ww-guide-widget flex flex-col items-center justify-center gap-5 py-16 px-6 text-center"
      style={{ ...GUIDE_WIDGET_LAYOUT_STYLE, background: "var(--color-background)" }}
    >
      <h2 className="ww-type-heading-md" style={{ color: "var(--text-secondary)" }}>
        Vi hittar dina rekommendationer
      </h2>

      <p className="ww-type-body-md ww-qa-thinking" aria-live="polite" style={{ color: "var(--text-tertiary)" }}>
        {phrase}...
      </p>

      <div className="self-stretch">
        <div
          className="h-2 overflow-hidden rounded-full"
          role="progressbar"
          aria-label="Laddar rekommendationer"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(progress)}
          style={{ background: "var(--color-border)" }}
        >
          <div
            className="h-full rounded-full"
            style={{
              width: `${Math.max(4, progress)}%`,
              background: "var(--color-primary)",
              transition: "width 220ms linear",
            }}
          />
        </div>
      </div>
    </div>
  );
}

function GuideWidgetInner({ storeId, postGuide, showMetadata }: InnerProps) {
  const [phase, setPhase] = React.useState<Phase>("intro");
  const [loadingMode, setLoadingMode] = React.useState<LoadingMode>("question");
  const [loadingStartedAt, setLoadingStartedAt] = React.useState<number>(Date.now());
  const [recommendationLoadingDone, setRecommendationLoadingDone] = React.useState(false);
  const [sessionId, setSessionId] = React.useState<string | null>(null);
  const [answers, setAnswers] = React.useState<Record<string, unknown>>({});
  const [answerHistory, setAnswerHistory] = React.useState<Array<Record<string, unknown>>>([{}]);
  const [currentQuestion, setCurrentQuestion] = React.useState<GuideResponse | null>(null);
  const [result, setResult] = React.useState<GuideResponse | null>(null);
  const [errorMsg, setErrorMsg] = React.useState<string | null>(null);
  const [inputValues, setInputValues] = React.useState<Record<string, string>>({});
  const [isExplanationOpen, setIsExplanationOpen] = React.useState(false);
  const abortRef = React.useRef<AbortController | null>(null);

  React.useEffect(() => {
    setIsExplanationOpen(false);
  }, [currentQuestion?.question_id]);

  React.useEffect(() => {
    if (!isExplanationOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsExplanationOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isExplanationOpen]);

  const submitAnswers = React.useCallback(
    async (nextAnswers: Record<string, unknown>, recordInHistory = true) => {
      abortRef.current?.abort();
      abortRef.current = new AbortController();

      const progress = currentQuestion?.progress;
      const isRecommendationLoading = Boolean(progress && progress.current >= progress.total);
      setLoadingMode(isRecommendationLoading ? "recommendation" : "question");
      setLoadingStartedAt(Date.now());
      setRecommendationLoadingDone(false);
      setPhase("loading");
      setErrorMsg(null);

      try {
        const { language, market } = getRuntimeLocale();
        const data = await postGuide(
          {
            session_id: sessionId,
            store_id: storeId,
            locale: { language, market },
            answers: nextAnswers,
          },
          abortRef.current.signal,
        );

        if (data.session_id) setSessionId(data.session_id);

        if (data.type === "question") {
          setCurrentQuestion(data);
          if (recordInHistory) {
            setAnswerHistory((prev) => {
              // Prevent duplicates when the same snapshot is re-submitted (e.g. skip).
              const latest = prev[prev.length - 1] ?? {};
              if (areAnswerSnapshotsEqual(latest, nextAnswers)) return prev;
              return [...prev, { ...nextAnswers }];
            });
          }
          setAnswers(nextAnswers);
          setInputValues({});
          setPhase("question");
        } else if (data.type === "result") {
          if (isRecommendationLoading) {
            setRecommendationLoadingDone(true);
            await new Promise<void>((resolve) => {
              window.setTimeout(resolve, 220);
            });
          }
          setResult(data);
          if (recordInHistory) {
            setAnswerHistory((prev) => {
              const latest = prev[prev.length - 1] ?? {};
              if (areAnswerSnapshotsEqual(latest, nextAnswers)) return prev;
              return [...prev, { ...nextAnswers }];
            });
          }
          setAnswers(nextAnswers);
          setPhase("result");
        } else {
          setErrorMsg(data.error || "Something went wrong");
          setPhase("error");
        }
      } catch (err) {
        if ((err as Error).name === "AbortError") return;
        setErrorMsg((err as Error).message || "Something went wrong");
        setPhase("error");
      }
    },
    [currentQuestion, postGuide, sessionId, storeId],
  );

  const handleStart = () => submitAnswers({});

  const handleOptionSelect = (opt: Record<string, unknown>) => {
    if (!currentQuestion?.question_id) return;
    const value = opt.value ?? opt.label ?? opt.id;
    submitAnswers({ ...answers, [currentQuestion.question_id]: value });
  };

  const handleInputSubmit = () => {
    if (!currentQuestion?.question_id) return;
    const merged = { ...answers };
    for (const [key, val] of Object.entries(inputValues)) {
      if (val.trim()) {
        const num = Number(val);
        merged[key] = Number.isFinite(num) ? num : val;
      }
    }
    submitAnswers(merged);
  };

  const handleNumericSubmit = () => {
    if (!currentQuestion?.question_id) return;
    const raw = inputValues[currentQuestion.question_id] ?? "";
    const num = Number(raw);
    if (!raw.trim() || !Number.isFinite(num)) return;
    submitAnswers({ ...answers, [currentQuestion.question_id]: num });
  };

  const handleRestart = () => {
    setPhase("intro");
    setSessionId(null);
    setAnswers({});
    setAnswerHistory([{}]);
    setCurrentQuestion(null);
    setResult(null);
    setErrorMsg(null);
    setInputValues({});
  };

  const handleBack = () => {
    if (answerHistory.length <= 1) return;
    const trimmedHistory = answerHistory.slice(0, -1);
    const previousSnapshot = trimmedHistory[trimmedHistory.length - 1] ?? {};
    setAnswerHistory(trimmedHistory);
    submitAnswers({ ...previousSnapshot }, false);
  };

  const handleBuy = (product: RecommendedProduct) => {
    if (typeof product.url !== "string" || !product.url.trim()) return;
    window.open(product.url, "_blank", "noopener,noreferrer");
  };

  // -- Render ----------------------------------------------------------------

  if (phase === "intro") {
    return (
      <div
        className="ww-guide-widget flex flex-col items-center justify-center gap-8 py-12 px-6 text-center"
        style={{ ...GUIDE_WIDGET_LAYOUT_STYLE, background: "var(--color-background)" }}
      >
        <h1 className="ww-type-heading-lg" style={{ color: "var(--text-secondary)" }}>
          Alpingaragets skidguide
        </h1>
        <ul className="flex flex-col gap-4 text-left">
          <li className="flex items-center gap-3">
            <span className="ww-type-body-md" style={{ color: "var(--text-secondary)" }}>
              Hitta den perfekta skidan
            </span>
          </li>
        </ul>
        <button type="button" className="ww-btn ww-btn-primary" onClick={handleStart}>
          Påbörja guiden
        </button>
      </div>
    );
  }

  if (phase === "loading") {
    if (loadingMode === "recommendation") {
      return (
        <RecommendationLoadingScreen
          startedAt={loadingStartedAt}
          forceComplete={recommendationLoadingDone}
        />
      );
    }

    return (
      <div
        className="ww-guide-widget flex flex-col items-center justify-center gap-4 py-16 px-6"
        style={{ ...GUIDE_WIDGET_LAYOUT_STYLE, background: "var(--color-background)" }}
      >
        <span className="ww-type-body-md ww-qa-thinking" style={{ color: "var(--text-tertiary)" }}>
          Tänker...
        </span>
      </div>
    );
  }

  if (phase === "error") {
    return (
      <div
        className="ww-guide-widget flex flex-col items-center justify-center gap-6 py-16 px-6 text-center"
        style={{ ...GUIDE_WIDGET_LAYOUT_STYLE, background: "var(--color-background)" }}
      >
        <p className="ww-type-body-md" style={{ color: "var(--text-secondary)" }}>
          {errorMsg || "Oj, något gick fel"}
        </p>
        <button type="button" className="ww-btn ww-btn-secondary" onClick={handleRestart}>
          Försök igen
        </button>
      </div>
    );
  }

  if (phase === "question" && currentQuestion) {
    const progress = currentQuestion.progress;
    const options = currentQuestion.options ?? [];
    const isInput = currentQuestion.input_type === "input" || currentQuestion.input_type === "form";
    const isNumeric = currentQuestion.input_type === "numeric";
    const explanation = (currentQuestion.explanation || "").trim();
    const explanationPopupId = `guide-explanation-${String(currentQuestion.question_id || "question").replace(/[^a-zA-Z0-9_-]/g, "-")}`;

    return (
      <div
        className="ww-guide-widget flex flex-col items-center gap-6 py-8 px-6"
        style={{ ...GUIDE_WIDGET_LAYOUT_STYLE, background: "var(--color-background)" }}
      >
        {progress && (
          <span className="ww-type-caption" style={{ color: "var(--text-tertiary)" }}>
            FRÅGA {progress.current} / {progress.total}
          </span>
        )}

        <div className="relative flex flex-col items-center gap-2 text-center">
          <div className="flex items-center justify-center gap-2">
            <h2 className="ww-type-heading-md" style={{ color: "var(--text-secondary)" }}>
              {currentQuestion.question}
            </h2>
            {explanation && (
              <button
                type="button"
                className="ww-icon-btn w-7 h-7 flex items-center justify-center rounded-full border"
                onClick={() => setIsExplanationOpen((prev) => !prev)}
                aria-label="Visa förklaring"
                aria-expanded={isExplanationOpen}
                aria-controls={explanationPopupId}
              >
                <span className="ww-type-body-sm font-semibold" style={{ color: "var(--text-secondary)" }}>
                  ?
                </span>
              </button>
            )}
          </div>
          {explanation && isExplanationOpen && (
            <div
              id={explanationPopupId}
              className="absolute top-full mt-2 w-[min(90vw,360px)] rounded-lg px-4 py-3 text-left shadow-sm z-10"
              style={{ border: "1px solid var(--color-border)", background: "var(--color-background)" }}
              role="dialog"
              aria-label="Förklaring"
            >
              <div className="flex items-start justify-between gap-3">
                <p className="ww-type-body-sm" style={{ color: "var(--text-secondary)" }}>
                  {explanation}
                </p>
                <button
                  type="button"
                  className="ww-icon-btn w-6 h-6 flex items-center justify-center rounded-full border flex-shrink-0"
                  onClick={() => setIsExplanationOpen(false)}
                  aria-label="Stäng förklaring"
                >
                  <span className="ww-type-caption" style={{ color: "var(--text-secondary)" }}>
                    ×
                  </span>
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="self-stretch flex flex-col gap-3">
          {!isInput && !isNumeric && options.map((opt, i) => (
            <GuideOptionCard key={i} option={opt} onSelect={handleOptionSelect} />
          ))}

          {isNumeric && (
            <div className="flex flex-col items-center gap-4">
              <input
                type="number"
                inputMode="numeric"
                value={inputValues[currentQuestion.question_id || ""] || ""}
                onChange={(e) =>
                  setInputValues((prev) => ({ ...prev, [currentQuestion.question_id || ""]: e.target.value }))
                }
                onKeyDown={(e) => { if (e.key === "Enter") handleNumericSubmit(); }}
                className="w-40 text-center bg-transparent outline-none ww-type-heading-md"
                style={{ color: "var(--text-secondary)", borderBottom: "2px solid var(--color-border)" }}
                autoFocus
              />
              <button
                type="button"
                className="ww-btn ww-btn-primary"
                onClick={handleNumericSubmit}
              >
                Fortsätt
              </button>
            </div>
          )}

          {isInput && (
            <>
              {options.map((opt) => {
                const key = String(opt.key || opt.id || opt.label || "");
                return (
                  <div
                    key={key}
                    className="flex items-center justify-between gap-4 px-5 py-4 rounded-lg"
                    style={{ border: "1px solid var(--color-border)", background: "var(--color-background)" }}
                  >
                    <label className="ww-type-body-md font-semibold" style={{ color: "var(--text-secondary)" }}>
                      {String(opt.label || key)}
                    </label>
                    <input
                      type="text"
                      value={inputValues[key] || ""}
                      onChange={(e) => setInputValues((prev) => ({ ...prev, [key]: e.target.value }))}
                      placeholder={String(opt.placeholder || "")}
                      className="w-32 text-right bg-transparent outline-none ww-type-body-md"
                      style={{ color: "var(--text-secondary)", borderBottom: "1px solid var(--color-border)" }}
                    />
                  </div>
                );
              })}
              <button
                type="button"
                className="ww-btn ww-btn-primary mt-2 self-center"
                onClick={handleInputSubmit}
              >
                Fortsätt
              </button>
            </>
          )}
        </div>

        {answerHistory.length > 1 && (
          <button
            type="button"
            onClick={handleBack}
            className="ww-type-body-sm underline"
            style={{ color: "var(--text-tertiary)", background: "none", border: "none", cursor: "pointer" }}
          >
            Gå tillbaka
          </button>
        )}
      </div>
    );
  }

  // phase === "result"
  const products = result?.recommended_products ?? [];
  const metadata = result?.metadata;
  const answerEntries = Object.entries(answers).filter(([, value]) => {
    if (value == null) return false;
    if (typeof value === "string") return value.trim().length > 0;
    if (Array.isArray(value)) return value.length > 0;
    return true;
  });
  const filterEntries =
    metadata && metadata.filters && typeof metadata.filters === "object"
      ? Object.entries(metadata.filters).filter(([, value]) => {
          if (value == null) return false;
          if (typeof value === "string") return value.trim().length > 0;
          if (Array.isArray(value)) return value.length > 0;
          return true;
        })
      : [];
  const meritEntries =
    metadata && metadata.merits && typeof metadata.merits === "object"
      ? Object.entries(metadata.merits).filter(([, value]) => {
          if (value == null) return false;
          if (typeof value === "string") return value.trim().length > 0;
          if (Array.isArray(value)) return value.length > 0;
          return true;
        })
      : [];
  const consideredProducts = Array.isArray(metadata?.products_considered)
    ? metadata.products_considered
    : [];
  return (
    <div
      className="ww-guide-widget flex flex-col items-center gap-8 py-8 px-6"
      style={{ ...GUIDE_WIDGET_LAYOUT_STYLE, background: "var(--color-background)" }}
    >
      <button
        type="button"
        onClick={handleRestart}
        className="flex items-center gap-3 self-center px-3 py-2"
        style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-tertiary)" }}
      >
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M15 18L9 12L15 6" />
        </svg>
        <span className="ww-type-body-md">Börja om</span>
      </button>

      {result?.customer_message && (
        <p
          className="ww-type-body-md text-center"
          style={{ color: "var(--text-secondary)" }}
        >
          {result.customer_message}
        </p>
      )}

      {showMetadata && answerEntries.length > 0 && (
        <section
          className="self-stretch rounded-lg px-4 py-4"
          style={{ border: "1px solid var(--color-border)", background: "var(--color-background)" }}
        >
          <h3 className="ww-type-body-md font-semibold" style={{ color: "var(--text-secondary)" }}>
            Your answers
          </h3>
          <div className="mt-3 flex flex-col gap-1">
            {answerEntries.map(([key, value]) => (
              <div key={key} className="ww-type-body-sm" style={{ color: "var(--text-secondary)" }}>
                <span style={{ color: "var(--text-tertiary)" }}>{formatAnswerLabel(key)}:</span>{" "}
                {formatMetadataValue(value)}
              </div>
            ))}
          </div>
        </section>
      )}

      {showMetadata && metadata && (
        <section
          className="self-stretch rounded-lg px-4 py-4"
          style={{ border: "1px solid var(--color-border)", background: "var(--color-background)" }}
        >
          <h3 className="ww-type-body-md font-semibold" style={{ color: "var(--text-secondary)" }}>
            Why these recommendations
          </h3>

          <div className="mt-3">
            <span className="ww-type-caption" style={{ color: "var(--text-tertiary)" }}>
              Category
            </span>
            <div className="mt-1 ww-type-body-sm" style={{ color: "var(--text-secondary)" }}>
              {metadata.category || "N/A"}
            </div>
          </div>

          {filterEntries.length > 0 && (
            <div className="mt-3">
              <span className="ww-type-caption" style={{ color: "var(--text-tertiary)" }}>
                Filters
              </span>
              <div className="mt-1 flex flex-col gap-1">
                {meritEntries.map(([key, value]) => (
                  <div key={`merit-${key}`} className="ww-type-body-sm" style={{ color: "var(--text-secondary)" }}>
                    <span style={{ color: "var(--text-tertiary)" }}>{key}:</span>{" "}
                    {formatMetadataValue(value)}
                  </div>
                ))}
                {filterEntries.map(([key, value]) => (
                  <div key={key} className="ww-type-body-sm" style={{ color: "var(--text-secondary)" }}>
                    <span style={{ color: "var(--text-tertiary)" }}>{key}:</span>{" "}
                    {formatMetadataValue(value)}
                  </div>
                ))}
              </div>
            </div>
          )}

          {consideredProducts.length > 0 && (
            <details className="mt-3">
              <summary className="ww-type-caption cursor-pointer" style={{ color: "var(--text-tertiary)" }}>
                Products considered ({consideredProducts.length})
              </summary>
              <div className="mt-2 flex flex-col gap-2">
                {consideredProducts.map((candidate, index) => {
                  const name = formatMetadataValue(candidate.name) || "Unnamed product";
                  const category = formatMetadataValue(candidate.category);
                  const price = formatMetadataValue(candidate.price);
                  const url = typeof candidate.url === "string" ? candidate.url : "";
                  return (
                    <div
                      key={`${candidate.product_id || "product"}-${index}`}
                      className="rounded px-3 py-2"
                      style={{ border: "1px solid var(--color-border)" }}
                    >
                      <div className="ww-type-body-sm" style={{ color: "var(--text-secondary)" }}>
                        {name}
                      </div>
                      {(category || price) && (
                        <div className="ww-type-caption mt-1" style={{ color: "var(--text-tertiary)" }}>
                          {[category ? `Category: ${category}` : "", price ? `Price: ${price}` : ""]
                            .filter(Boolean)
                            .join(" • ")}
                        </div>
                      )}
                      {url && (
                        <a
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ww-type-caption inline-block mt-1 underline"
                          style={{ color: "var(--text-tertiary)" }}
                        >
                          View product
                        </a>
                      )}
                    </div>
                  );
                })}
              </div>
            </details>
          )}
        </section>
      )}

      {products.length > 0 && (
        <div className={`self-stretch grid gap-8 ${products.length > 1 ? "sm:grid-cols-2 grid-cols-1" : "grid-cols-1"}`}>
          {products.map((product) => (
            <GuideResultCard
              key={product.id}
              product={product}
              onBuy={() => handleBuy(product)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
